import { QueryClient } from '@tanstack/react-query';
import { getContentDataProvider } from './ContentDataProvider';
/**
 * Locations are the one case where one "query" can involve multipe data providers. Within a location
 * (i.e. via the ContentNavigationProvider), all queries involve a single data provider, but since
 * locations can be the root for a custom data provider, and the custom data provider itself must
 * define how to get said root location Item, a query representing an array of locations thus may
 * involve a mixture of different data providers.
 *
 * This function will split up the identifiers by relevant data provider, fetch the items for each
 * group, and then put the results back in the correct order before returning.
 * @param identifiers The locations being fetched
 * @param dataProvider The data provider to use for locations without an attached provider
 * @returns The location Items in order
 */
export const getMixedLocations = async (identifiers, dataProvider = getContentDataProvider()) => {
  const requestGroups = [];

  // Group identifiers by common data provider
  identifiers.forEach((identifier, index) => {
    const thisDataProvider = identifier.dataProvider ?? dataProvider;
    const existingGroup = requestGroups.find(rg => rg.dataProvider === thisDataProvider);
    const thisGroup = existingGroup ?? {
      dataProvider: thisDataProvider,
      requests: []
    };
    if (!existingGroup) {
      requestGroups.push(thisGroup);
    }
    thisGroup.requests.push({
      originalIndex: index,
      identifier
    });
  });
  const results = new Array(identifiers.length);

  // Fetch each group via a single getItemsByIdentier call
  for (const requestGroup of requestGroups) {
    const identifiers = requestGroup.requests.map(req => req.identifier);
    const theseResults = await requestGroup.dataProvider.getItemsByIdentifier(identifiers);

    // Put them back in the right order
    requestGroup.requests.forEach((req, index) => {
      results[req.originalIndex] = theseResults[index];
    });
  }
  return results;
};

/*
This is the QueryClient that all content component queries need to use for proper cache sharing,
isolation from potential consumer/Nova RQ QueryClients, etc. Due to RQ 3 changes, every content
component needs to be wrapped in a QueryClientProvider that takes in this QueryClient.
*/
export const contentQueryClient = new QueryClient();
//# sourceMappingURL=common.js.map